const LAST_RELOAD_KEY = "productlane:stale-chunk-reload-at"
export const STALE_CHUNK_RELOAD_COOLDOWN_MS = 30_000

/** One reload per cooldown, so a chunk that is really broken cannot loop. */
export function shouldReloadForStaleChunk(
	lastReloadAt: number | null,
	now: number,
): boolean {
	return (
		lastReloadAt === null || now - lastReloadAt > STALE_CHUNK_RELOAD_COOLDOWN_MS
	)
}

function readLastReload(): number | null {
	try {
		const value = Number(window.sessionStorage.getItem(LAST_RELOAD_KEY))
		return Number.isFinite(value) && value > 0 ? value : null
	} catch {
		return null
	}
}

// Without storage the cooldown can't hold, so the caller must not reload.
function rememberReload(now: number): boolean {
	try {
		window.sessionStorage.setItem(LAST_RELOAD_KEY, String(now))
		return true
	} catch {
		return false
	}
}

const STALE_CHUNK_MESSAGES = [
	"is not a valid JavaScript MIME type",
	"Failed to fetch dynamically imported module",
	"Importing a module script failed",
	"error loading dynamically imported module",
]

/** Matches the browser errors for a module chunk that came back as HTML. */
export function isStaleChunkError(reason: unknown): boolean {
	const message = reason instanceof Error ? reason.message : String(reason)
	return STALE_CHUNK_MESSAGES.some((text) => message.includes(text))
}

let reloading = false

/**
 * True once a stale-chunk reload has started. Cancelling Vite's preload error
 * resolves the import to undefined, so callers throw until the page unloads.
 */
export function isReloadingForStaleChunk(): boolean {
	return reloading
}

function reloadForStaleChunk(event: Event) {
	const now = Date.now()
	if (!shouldReloadForStaleChunk(readLastReload(), now)) return
	if (!rememberReload(now)) return
	reloading = true
	event.preventDefault()
	window.location.reload()
}

// A tab opened before a deploy asks for chunk files the new build no longer
// has, and gets the HTML page back. A reload picks up the new build.
// Imports outside Vite's preload helper only surface as unhandled rejections.
export function installStaleChunkReload() {
	window.addEventListener("vite:preloadError", reloadForStaleChunk)
	window.addEventListener("unhandledrejection", (event) => {
		if (isStaleChunkError(event.reason)) reloadForStaleChunk(event)
	})
}
